import {
  CreateCustomCode,
  ReadCustomCode,
  UpdateCustomCode,
} from '@tinct/core-api-models';
import {ApiClient} from '../api-client/api-client';

/**
 * The two custom-code scopes. Workspace entries load on every hosted landing
 * page of the organization; page entries layer AFTER them on one page only.
 * Both scopes expose the exact same routes and shapes.
 */
export type CustomCodeScope =
  | { organizationId: string }
  | { landingPageId: string };

const basePath = (scope: CustomCodeScope): string =>
  'organizationId' in scope
    ? `/api/v1/organizations/${scope.organizationId}/custom-codes`
    : `/api/v1/landing-pages/${scope.landingPageId}/custom-codes`;

export class CustomCodesEndpoint {
  constructor(private client: ApiClient) {}

  /** The scope's entries, ordered by position. */
  async getCustomCodes(scope: CustomCodeScope): Promise<ReadCustomCode[]> {
    return this.client.getById<ReadCustomCode[]>(basePath(scope));
  }

  async createCustomCode(
    scope: CustomCodeScope,
    data: CreateCustomCode,
  ): Promise<ReadCustomCode> {
    return this.client.post<ReadCustomCode>(basePath(scope), data);
  }

  /** Only sent fields are applied. */
  async patchCustomCode(
    scope: CustomCodeScope,
    codeId: string,
    data: UpdateCustomCode,
  ): Promise<ReadCustomCode> {
    return this.client.patch<ReadCustomCode>(
      `${basePath(scope)}/${codeId}`,
      data,
    );
  }

  async deleteCustomCode(scope: CustomCodeScope, codeId: string): Promise<void> {
    return this.client.delete<void>(`${basePath(scope)}/${codeId}`);
  }

  /** The scope's ids in the new order; returns the reordered list. */
  async reorderCustomCodes(
    scope: CustomCodeScope,
    ids: string[],
  ): Promise<ReadCustomCode[]> {
    return this.client.put<ReadCustomCode[]>(`${basePath(scope)}/order`, {
      ids,
    });
  }
}
